<template>
    <md-card class="ql-page" id="ql-page" :key="handler">
        <md-card-media-cover :id="`cardCover${pageNumber}`" ref="cardCover" class="ql-page-cover">
            <div class="ql-pdf-page-wrapper" @click="handleClick">
                <project-pdf-thumbnail-page class="ql-pdf-page" :class="{'disable-text': disableText}"
                    v-if="ready"
                    :page="page" 
                    :pageWidth="pageWidth"
                    :pageHeight="pageHeight"
                    :currentLayer="currentLayer"
                    :issrc="issrc"
                    @pdfLoaded="pdfLoaded"
                    @openDialog="openDialog"
                />
            </div>
            <div class="ql-page-number" @click="handlePageOutClick()">
                <md-input-container @click="editNumber" ref="inputContainer" v-if="isAdministrator && !versionID">
                    <md-input ref="inputPageName" v-if="project.pdfPageNumbers && project.pdfPageNumbers[pageIndex - 1] && 
                    project.pdfPageNumbers[pageIndex - 1].name && project.pdfPageNumbers[pageIndex - 1].name" :value="project.pdfPageNumbers[pageIndex - 1].name || pageNumber" required v-keydown="handleKeyDown" @blur="pdfpageRename" :disabled="locked"></md-input>
                    <md-input ref="inputPageName" v-else :value="pageNumber" required v-keydown="handleKeyDown" @blur="pdfpageRename" :disabled="locked"></md-input>
                </md-input-container>
                <p v-else-if="versionID">
                    {{ this.currentVersion.pdfPageNumbers && this.currentVersion.pdfPageNumbers[pageIndex - 1] && 
                    this.currentVersion.pdfPageNumbers[pageIndex - 1].name && this.currentVersion.pdfPageNumbers[pageIndex - 1].name || pageNumber }}
                </p>
                <p v-else>
                    {{ this.project.pdfPageNumbers && this.project.pdfPageNumbers[pageIndex - 1] && 
                    this.project.pdfPageNumbers[pageIndex - 1].name && this.project.pdfPageNumbers[pageIndex - 1].name || pageNumber }}
                </p>
            </div>

            <p v-if="!ready && !versionID">Loading PDF...</p>
            <resize-sensor @resize="resize"></resize-sensor>
        </md-card-media-cover>
    </md-card>
</template>

<script>
import { cueGeometryOk } from '@/lib/cue-geometry';
import resizeSensor from 'vue-resize-sensor';
import { mapGetters } from 'vuex';
import { bookmarkRenames } from '@/lib/bookmark-labels';
import { shouldWarnNoSeed } from '@/lib/autoNumberWarning';
import ProjectPdfThumbnailPage from '@/components/project/pdf-thumbnail-page';
import keydownDirective from '../../directives/keydown';
import MaterialSymbolsLightCheckBox from '../../assets/img/MaterialSymbolsLightCheckBox.svg';

const comparator = (a, b) => {
    if (a.y === b.y) {
        if (a.x === b.x || a.x > b.x) {
            return 1;
        }
        return 0;
    } else if (a.y > b.y) {
        return 1;
    }
    return 0;
};

const ProjectPage = {
    name: 'projectThumbnailPage',
    directives: {
        keydown: keydownDirective
    },
    props: ['page', 'cues', 'locked', 'bookmarked', 'bookmarkNavOpened', 'issrc', 'pageIndex'],
    data: function() {
        return {
            pageNumber: this.$props.page.pageNumber,
            pageContainerId: 'pageContainer' + this.$props.page.pageNumber,
            ready: false,
            pageReady: false,
            disableText: false,
            scale: 1,
            addingCue: false,
            addCueX: 0,
            addCueY: 0,
            cueToAdd: null,
            offsetX: 0,
            offsetY: 0,
            pageWidth: 0,
            pageHeight: 0,
            pdfHeight: 0,
            pdfWidth: 0,
            originWidth: 0,
            originHeight: 0,
            cueMarkerWidth: 6,
            cueMarkerHeight: 6,
            handler: 0,
            isEditNumber: false
        };
    },
    watch: {
        bookmarkNavOpened() {
            this.handler++;
        }
    },
    computed: {
        ...mapGetters([
            'project',
            'user',
            'collaboratorByUID',
            'currentVersion',
            'layerByCue',
            'visibleCues',
            'editableLayers',
            'currentLayer',
            'currentNoteLayer',
            'notesByPage',
            'cuesByPageLayer',
            'showNotesLayerIDs',
            'selectedTool',
            'showMode',
            'ownerUID',
            'bookmarks',
            'selectedThumbnailView',
            'projectTotalPages'
        ]),
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        displayCues: function() {
            return this.visibleCues(this.pageNumber);
        },
        selectionCues: function() {
            return this.displayCues.filter(c => c.type === 'selection');
        },
        positionCues: function() {
            return this.displayCues.filter(c => c.type === 'position');
        },
        displayNotes: function() {
            return this.notesByPage(this.pageNumber);
        },
        displayLayerCues: function() {
            return this.cuesByPageLayer(this.pageNumber);
        },
        bookmarkStyle: function() {
            return {
                position: 'absolute',
                top: '0px',
                left: (this.pageWidth - 46) + 'px',
                zIndex: '9999',
                color: '#5ecfdf',
                transition: 'none'
            };
        },
        isAdministrator() {
            const collab = this.collaboratorByUID(this.user.uid);
            if (this.ownerUID === this.user.uid || (collab && collab.type === 'administrator')) {
                const style = document.createElement('style');
                style.innerHTML = `
                    .ql-pdf-page-wrapper .ql-pdf-page::before {
                        background: #f0eded !important;
                        background-image: url(${MaterialSymbolsLightCheckBox}) !important;
                        background-size: cover !important;
                    }
                `;
                document.head.appendChild(style);
            } else {
                const style = document.createElement('style');
                style.innerHTML = `
                    .ql-pdf-page-wrapper .ql-pdf-page::before {
                        background: none !important;
                        background-image: none !important;
                        background-size: unset !important;
                    }
                `;
                document.head.appendChild(style);
            }
            return this.ownerUID === this.user.uid || (collab && collab.type === 'administrator');
        },
        totalPages: function() {
            if (this.versionID) {
                return this.projectTotalPages || 0;
            }
            
            return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                ? this.project.pdfPageNumbers.length 
                : 0;
        }
    },
    methods: {
        handleClick() {
            if (!this.locked && this.isAdministrator) {
                this.$emit('click');
                const style = document.createElement('style');
                style.innerHTML = `
                    .ql-pdf-page-wrapper .ql-pdf-page::before {
                        background: #f0eded !important;
                        background-image: url(${MaterialSymbolsLightCheckBox}) !important;
                        background-size: cover !important;
                    }
                `;
                document.head.appendChild(style);
            } else if (!this.isAdministrator) {
                const style = document.createElement('style');
                style.innerHTML = `
                    .ql-pdf-page-wrapper .ql-pdf-page::before {
                        background: none !important;
                        background-image: none !important;
                        background-size: unset !important;
                    }
                `;
                document.head.appendChild(style);
            }
        },
        handlePageOutClick() {
            if (!this.locked && this.isAdministrator && !this.versionID) {
                this.$emit('thumbnailSelectionClick', []);
            }
        },
        editNumber() {
            if (!this.locked) {
                this.$refs.inputPageName.focus();
            }
        },
        pdfpageRename(e) {
            this.$store.dispatch('setLoader', {
                type: true
            });
            const pdfPages = this.project.pdfPageNumbers;
            const oldLabels = pdfPages.map(p => p.name);
            if (pdfPages.length > 0) {
                pdfPages[this.pageIndex - 1].name = e;
            }
            this.$store.dispatch('renamePdfPages', {
                pdfPage: pdfPages
            }).then(() => {
                this.working = false;
                this.isEditNumber = false;
            });
            // Only a bookmark still named "page <old label>" follows; a renamed one keeps its name.
            const newLabels = pdfPages.map(p => p.name);
            for (const rename of bookmarkRenames(this.bookmarks, oldLabels, newLabels)) {
                this.$store.dispatch('updateBookmark', rename);
            }
            this.$store.dispatch('setLoader', { type: false });
        },
        handleKeyDown(e) {
            if (e.keyCode === 13) {
                document.querySelector('.page-viewer').focus();
            }
        },
        openDialog(ref) {
            if (this.selectedThumbnailView !== 'true') {
                this.$refs[ref].open();
            }
        },
        closeDialog(ref) {
            if (this.selectedThumbnailView !== 'true') {
                this.$refs[ref].close();
            }
        },
        getPDFViewport() {
            return this.page.pdfPage.getViewport(this.scale, this.page.pdfPage.pageInfo.rotate);
        },
        readyPage() {
            this.ready = false;
            const bounds = document.querySelector('.ql-page-cover').getBoundingClientRect();
            const vp = this.getPDFViewport();

            let ph = window.innerHeight - bounds.top;

            let pw = (vp.width * ph) / vp.height;

            if (pw > bounds.width) {
                ph *= bounds.width / pw;
                pw = bounds.width;
            }

            this.scale = pw / Math.min(bounds.width, vp.width);

            this.originWidth = vp.width;
            this.originHeight = vp.height;

            this.pageWidth = pw;
            this.pageHeight = ph;
            this.ready = true;
            if (!this.isAdministrator) {
                const style = document.createElement('style');
                style.innerHTML = `
                    .ql-pdf-page-wrapper .ql-pdf-page::before {
                        background: none !important;
                        background-image: none !important;
                        background-size: unset !important;
                    }
                `;
                document.head.appendChild(style);
            }
        },
        setScale(scale) {
            this.scale = scale;
        },
        pdfLoaded(data) {
            this.setOffset();

            this.$emit('pageLoaded', {
                ...data, 
                offsetX: this.offsetX, 
                offsetY: this.offsetY,
                width: this.pdfWidth,
                height: this.pdfHeight,
                pageWidth: this.pageWidth,
                pageHeight: this.pageHeight,
                markerWidth: this.cueMarkerWidth,
                cueMarkerHeight: this.cueMarkerHeight
            });

            this.pageReady = true;
        },
        pageColumnWidth() {
            if (this.pageReady) {
                document.querySelector('.ql-project-header-column-left').style.minWidth = `${this.pageWidth}px`;
                return {
                    maxWidth: `${this.pageWidth}px`
                };
            }
            return {};
        },
        cueCreated(tempCue) {
            if (this.locked || this.addingCue) {
                return;
            }
            // Never save a cue without a real position (lib/cue-geometry.js).
            if (!cueGeometryOk(tempCue)) {
                return;
            }

            const filter = this.editableLayers.filter(l => l.id === this.currentLayer.id);
            if (filter.length <= 0) {
                return;
            }

            this.$nextTick(() => {
                this.addingCue = false;
                this.cueToAdd = {
                    ...tempCue,
                    title: '',
                    description: tempCue.text || '',
                    pageNumber: this.pageNumber
                };
                this.addCueX = tempCue.x + (this.offsetX - 25);
                this.addCueY = tempCue.y - 5;
                this.addingCue = true;
                this.addCue();
            });
        },
        addCue() {
            if (this.locked) return;

            if (this.cueToAdd.x > 1 || this.cueToAdd.y > 1) {
                this.cueToAdd.x /= this.pageWidth;
                if (this.cueToAdd.x > 1) this.cueToAdd.x = 1;
                this.cueToAdd.y /= this.pageHeight;
                if (this.cueToAdd.y > 1) this.cueToAdd.y = 1;
            }
            this.$store.dispatch('addCue', this.cueToAdd).then((cueData) => {
                // Auto Numbering was on but had no starting number, so the cue
                // came out unnumbered. Say so ONCE per layer -- see
                // autoNumberWarning.js for why not once per cue.
                if (cueData.autoNumberSkipped === 'no-seed' && this.$toast
                    && shouldWarnNoSeed(this.project && this.project.id, cueData.layerID)) {
                    this.$toast.info('Auto Numbering is on for this layer but has no starting number, so the cue was added without one. Set one in the layer settings.');
                }
                const numberElement = document.querySelector('#cue-header-number-' + cueData.id);
                const labelElement = document.querySelector('#cue-header-label-' + cueData.id);
                const timeElement = document.querySelector('#cue-header-time-' + cueData.id);

                if (cueData.autoNumbered) {
                    // The number field was auto-filled, so move focus to the
                    // field after it (label, then time). If no subsequent field
                    // is visible, leave the cue unfocused.
                    if (labelElement) {
                        labelElement.click();
                    } else if (timeElement) {
                        timeElement.click();
                    }
                } else if (numberElement) {
                    numberElement.click();
                } else if (labelElement) {
                    labelElement.click();
                }
                this.addingCue = false;
                this.cueToAdd = null;
                this.sortCues();
            });
        },
        destroyCue(cueToDestroy) {
            this.$store.dispatch('deletedCue', cueToDestroy);
        },
        updateCue(cueToUpdate) {
            if (cueToUpdate.x > 1 || cueToUpdate.y > 1) {
                cueToUpdate.x /= this.pageWidth;
                if (cueToUpdate.x > 1) cueToUpdate.x = 1;
                cueToUpdate.y /= this.pageHeight;
                if (cueToUpdate.y > 1) cueToUpdate.y = 1;
            }
            this.$store.dispatch('updateCue', cueToUpdate);
        },
        updateNote(noteToUpdate) {
            if (noteToUpdate.text !== '') {
                if (noteToUpdate.x > 1 || noteToUpdate.y > 1) {
                    noteToUpdate.x /= this.pageWidth;
                    if (noteToUpdate.x > 1) noteToUpdate.x = 1;
                    noteToUpdate.y /= this.pageHeight;
                    if (noteToUpdate.y > 1) noteToUpdate.y = 1;
                }
                this.$store.dispatch('updateUserNote', {note: noteToUpdate});
            }
        },
        addNote({data, type}) {
            if (!this.user.active_pay) {
                if (!this.issrc && !this.project.unlocked) {
                    this.openDialog('no-collaborator-dialog');
                    return;
                }
            }
            if (!this.currentNoteLayer || !this.showNotesLayerIDs.includes(this.currentLayer.id)) {
                this.openDialog('no-layers-dialog');
                return;
            }
            if (this.locked) return;

            this.$nextTick(() => {
                const note = {
                    ...data,
                    type,
                    pageNumber: this.pageNumber
                };
                this.$store.dispatch('addUserNote', {
                    note,
                    layer: this.currentNoteLayer
                }).then((noteData) => {
                    if (noteData.type !== 'image' && noteData.type !== 'text' && noteData.type !== 'stamp') return;
                    setTimeout(() => {
                        const noteRef = document.querySelector('#' + noteData.type + '-note-' + noteData.id);
                        noteRef.parentElement.click();
                        noteRef.focus();
                    }, 100);
                });
            });
        },
        cancelAddCue() {
            this.addingCue = false;
            this.cueToAdd = null;
        },
        sortCues() {
            if (this.cues) {
                this.cues.sort(comparator);
            }
        },

        cueColor(cue) {
            const layer = this.layerByCue(cue);
            if (layer) return layer.color;

            return 'black';
        },
        setOffset() {
            const page = document.querySelector('.ql-pdf-page');
            if (page) {
                const bounds = page.getBoundingClientRect();

                this.pdfHeight = bounds.height;
                this.pdfWidth = bounds.width;
                this.offsetY = bounds.y;
                this.$store.dispatch('updatePageOffset', bounds.x);
            }
        },
        resize() {
            this.setOffset();
            if (this.pageReady) {
                this.$emit('pageLoaded', {
                    offsetX: this.offsetX, 
                    offsetY: this.offsetY,
                    width: this.pdfWidth,
                    height: this.pdfHeight,
                    pageWidth: this.pageWidth,
                    pageHeight: this.pageHeight,
                    markerWidth: this.cueMarkerWidth,
                    cueMarkerHeight: this.cueMarkerHeight
                });
            }
        },
        isCueEditable(cue) {
            const layer = this.layerByCue(cue);
            if (layer) {
                const filter = this.editableLayers.filter(l => l.id === layer.id);
                return filter.length > 0;
            }
            return false;
        },
        isNoteEditable(note) {
            const filter = this.editableLayers.filter(l => l.id === note.layer);
            return filter.length > 0;
        },
        toggleBookmarkNav() {
            this.$emit('toggleBookmarkNav');
        },
        togglePageBookmark() {
            if (this.bookmarked) {
                this.$store.dispatch('removeBookmark', {
                    pageNumber: this.pageNumber
                });
            } else {
                this.$store.dispatch('addBookmark', {
                    pageNumber: this.pageNumber
                });
                if (!this.bookmarkNavOpened) {
                    this.toggleBookmarkNav();
                }
            }
        },
        handleClickOutside(event) {
            const target = event.target;
            const parentDiv = target.closest('.textLayer');
            const parentSubDiv1 = target.closest('.md-card.ql-page.md-flex.md-theme-default');
            const parentSubDiv2 = target.closest('.md-layout.ql-page-layout.ql-thumbail-view.md-row.md-gutter-12');
            if (parentDiv) {
                return true;
            } else if (parentSubDiv1 || parentSubDiv2) {
                this.$emit('thumbnailSelectionClick', []);
            }
            return false;
        }
    },
    components: {
        'resize-sensor': resizeSensor,
        'project-pdf-thumbnail-page': ProjectPdfThumbnailPage
    },
    mounted: function() {
        this.readyPage();
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy: function() {
        document.removeEventListener('click', this.handleClickOutside);
    }
};

export default ProjectPage;
</script>

<style lang="scss">


.page-viewer {
	padding: 20px 0px;
    outline: none;
}

.marker-container, .ql-note-container {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1001;
    pointer-events: none;
}
.marker-container {
    z-index: 1002 !important;
}

.ql-add-cue-button {
    position: absolute;
    z-index: 1001;
}


.disable-text {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}

.ql-thumbail-view{
    .md-theme-show-mode{
        &.ql-page{
            .ql-pdf-page{
                &::before {
                    filter: invert(0) grayscale(1);
                    background-color: transparent !important;
                }
            }
        }
    }
    &.ql-page-layout {
        .md-theme-show-mode{
            .canvasWrapper {
                filter: invert(88%);
                -webkit-filter: invert(88%);
            }
        }
        .ql-page {
            max-width: 25%;
            flex: unset;
            padding-left: 16px;
            padding-right: 16px;
            padding-bottom: 16px;
            box-shadow: none;
            background: transparent;
    
             
            &.selected-page-number {
                .ql-pdf-page{   
                    &::before {
                        opacity: 1;
                        visibility: visible;
                    }
                }
            }
           
        }
    
        .canvasWrapper {
            width: 100%;
            height: 100%;
            text-align: center;
            position: relative;
            padding-top: 100%;            
            transition: all 0.2s ease;
    
            canvas {
                cursor: crosshair;
                position: absolute;
                top: 0;
                left: 0;
                width: 100% !important;
                height: 100% !important;
                object-fit: contain;
            }
        }
    
        .textLayer{
            z-index: 1;
            cursor: pointer;
        }
    }
    
    .ql-page {
        .ql-page-cover {
            background-color:#f0eded;
        }
    
        .ql-pdf-page {
            margin: 0 auto;
            position: relative;
            width: 100% !important;
            height: 100% !important;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 24px;
                height: 24px;
                background: #f0eded;
                background-image: url("../../assets/img/MaterialSymbolsLightCheckBox.svg");
                background-size: cover;
                border-radius: 0 0 3px 0;
                opacity: 0;
                visibility: hidden;
            }
        }
        
        .ql-pdf-page-wrapper{
            border: 3px solid transparent;
            border-radius: 8px;
            padding: 2px;
            width: 100%;
            position: relative;
            
            
            &::before {
                content: '';
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: rgba(0,0,0,0.2);
                z-index: 1;
                position: absolute;
                opacity: 0;
                transition: all 0.5s ease;
            }

            &:hover {
                border-color: #999;

                .ql-pdf-page{
                    &::before {
                        opacity: 1;
                        visibility: visible;
                        z-index: 2;
                    }
                }

                &::before {
                    opacity: 1;
                }
            }
        }
    }
    
    .ql-page-number {
        display: flex;
        justify-content: center;
        align-items: center;
    
        p,input {
            font-size: 16px;
            background-color: transparent;
            padding: 8px 9px;
            border-radius: 4px;
            line-height: normal;
            color: rgb(0, 0, 0);
            transition: all 0.5s ease;
            display: block;
            max-width: 100px;
            text-align: center;
            margin-inline: auto;

            &:hover {
                background-color: #1976D2;
                color: rgb(255,255,255);
            }
        }

        .md-input-container{
            &::after {
                display: none;
            }
        }
    }

}



.md-theme-show-mode{
    .ql-page {
        .ql-page-cover {
            background-color:#424242;
        }

        .ql-pdf-page-wrapper {
            &:hover{
                border-color: #999;
            }
        }

        .ql-page-number {
            p,input{
                &:hover {
                    color: #fff;
                    background-color: #666;
                }
            }
        }
    }

    .ql-page-number {
        p {
            color: #fff;
        }
    }
}
</style>


// WEBPACK FOOTER //
// thumbnail-page.vue?3bc8eefe